import React from 'react';
import { Divider, Grid, Typography } from '@mui/material';
import { currencyFormatter, formatGroupedExactAmount, NUMERIC_TEXT_SX } from '../utils/currencyFormat';
import { DEFAULT_CURRENCY_CODE } from '../constants/defaults';
import formatCurrencyWords from '../utils/formatCurrencyWords';
import { useTranslatedMessages } from '../hooks/useTranslatedMessages';

// Reusable SummaryRow component
const SummaryRow = ({
  label,
  value,
  currencyCode,
  testId,
  isBold = false,
  color = 'inherit',
  prefix = '',
  suffix = '',
  hideIfZero = false,
  exact = false,
}) => {
  if (hideIfZero && value === 0) return null;
  const formattedNumber = exact ? formatGroupedExactAmount(value) : currencyFormatter().format(value);
  const formattedValue = `${prefix}${formattedNumber}${suffix}`;

  return (
    <>
      <Grid size={6}>
        <Typography fontWeight={isBold ? 'bold' : 'normal'}>{label}</Typography>
      </Grid>
      <Grid size={2}>
        <Typography fontWeight={isBold ? 'bold' : 'normal'}>{currencyCode}</Typography>
      </Grid>
      <Grid size={4} style={{ textAlign: 'right' }}>
        <Typography
          data-testid={testId}
          variant="body1"
          color={color}
          fontWeight={isBold ? 'bold' : 'normal'}
          sx={NUMERIC_TEXT_SX}
        >
          {'\u200E'}
          {formattedValue}
          {'\u200E'}
        </Typography>
      </Grid>
    </>
  );
};

const DocumentSummarySection = ({
  itemsSubtotal,
  subtotal,
  totalItemAllowances,
  totalItemCharges,
  totalExcludingTax,
  totalIncludingTax,
  totalTaxAmount,
  taxInAED,
  totalPayableAmount,
  documentCurrencyCode,

  headerCharges = 0,
  headerAllowance = 0,

  totalTaxableAmount = 0,
  totalNonTaxableAmount = 0,
  taxBreakdown = {},
  payableRoundingAmount,
}) => {
  const { eInvoiceForm, isRTL } = useTranslatedMessages();
  const { document_summary: messages } = eInvoiceForm;
  const currencyCode = documentCurrencyCode;

  const { allowances: documentAllowanceSummary, charges: documentChargeSummary } = taxBreakdown;
  return (
    <Grid container spacing={1.5}>
      <SummaryRow
        label={messages.item_subtotal}
        value={itemsSubtotal}
        currencyCode={documentCurrencyCode}
        testId="items-subtotal-value"
        exact
      />

      <SummaryRow
        label={messages.total_item_allowance}
        value={totalItemAllowances}
        currencyCode={currencyCode}
        testId="total-item-discount-value"
        color="error"
        prefix="-"
        hideIfZero
        exact
      />

      <SummaryRow
        label={messages.total_item_charge}
        value={totalItemCharges}
        currencyCode={currencyCode}
        testId="total-item-charges-value"
        hideIfZero
        exact
      />

      <SummaryRow
        label={messages.items_net_total}
        value={subtotal}
        currencyCode={currencyCode}
        testId="total-items-net-value"
        isBold={true}
      />

      <Grid size={12}>
        <Divider />
      </Grid>

      {/* Document-level Allowances by Tax Category */}
      {Object.keys(documentAllowanceSummary || {}).map((taxKey) => {
        const breakdown = documentAllowanceSummary[taxKey];
        if (breakdown.taxableAmount > 0) {
          return (
            <SummaryRow
              key={`allowance-${taxKey}`}
              label={messages.document_level_allowance
                .replace('{category}', breakdown.category)
                .replace('{rate}', `${breakdown.rate}`)}
              value={breakdown.taxableAmount}
              currencyCode={currencyCode}
              testId={`allowance-${taxKey}-value`}
              color="error"
              prefix="-"
            />
          );
        }
        return null;
      })}

      {/* Document-level Charges by Tax Category */}
      {Object.keys(documentChargeSummary || {}).map((taxKey) => {
        const breakdown = documentChargeSummary[taxKey];
        if (breakdown.taxableAmount !== 0) {
          return (
            <SummaryRow
              key={`charge-${taxKey}`}
              label={messages.document_level_charge
                .replace('{category}', breakdown.category)
                .replace('{rate}', `${breakdown.rate}`)}
              value={breakdown.taxableAmount}
              currencyCode={currencyCode}
              testId={`charge-${taxKey}-value`}
            />
          );
        }
        return null;
      })}

      <SummaryRow
        label={messages.total_document_allowance}
        value={headerAllowance}
        currencyCode={currencyCode}
        testId="header-allowance-value"
        color="error"
        prefix="-"
        hideIfZero
      />

      <SummaryRow
        label={messages.total_document_charge}
        value={headerCharges}
        currencyCode={currencyCode}
        testId="header-charge-value"
        hideIfZero
      />

      <SummaryRow
        label={messages.total_non_taxable_amount}
        value={totalNonTaxableAmount}
        currencyCode={currencyCode}
        testId="total-non-taxable-amount-value"
        hideIfZero
        exact
      />

      <SummaryRow
        label={messages.total_taxable_amount}
        value={totalTaxableAmount}
        currencyCode={currencyCode}
        testId="total-taxable-amount-value"
        hideIfZero
        exact
      />

      <SummaryRow
        label={messages.total_excluding_tax}
        value={totalExcludingTax}
        currencyCode={currencyCode}
        testId="total-excluding-tax-value"
        isBold
      />

      <SummaryRow
        label={messages.total_tax_amount}
        value={totalTaxAmount}
        currencyCode={currencyCode}
        testId="total-tax-amount-value"
        isBold
      />

      {taxInAED != null && (
        <SummaryRow
          label={messages.total_tax_amount_with_code.replace('{code}', DEFAULT_CURRENCY_CODE)}
          value={taxInAED}
          currencyCode={DEFAULT_CURRENCY_CODE}
          testId="total-tax-aed-value"
          isBold
        />
      )}

      <SummaryRow
        label={messages.total_including_tax}
        value={totalIncludingTax}
        currencyCode={currencyCode}
        testId="total-including-tax-value"
        isBold
      />

      {payableRoundingAmount != null && payableRoundingAmount !== 0 && (
        <SummaryRow
          label={messages.payable_rounding_amount}
          value={payableRoundingAmount}
          currencyCode={currencyCode}
          testId="payable-rounding-amount-value"
          isBold
        />
      )}

      <Grid size={12}>
        <Divider />
      </Grid>

      <SummaryRow
        label={messages.grand_total}
        value={totalPayableAmount}
        currencyCode={currencyCode}
        testId="grand-total-value"
        isBold
      />

      <Grid size={12}>
        <Divider />
      </Grid>

      <Grid size={12} style={{ textAlign: isRTL ? 'left' : 'right', direction: 'ltr' }}>
        <Typography data-testid="currency-words-value" variant="body1">
          {!isNaN(totalPayableAmount) && formatCurrencyWords(totalPayableAmount, currencyCode)}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default DocumentSummarySection;
